import * as Tooltip from "@radix-ui/react-tooltip";
import classNames from "classnames/bind";
import { motion } from "framer-motion";
import React, { useEffect, useRef, useState } from "react";
import FontAwesome from "react-fontawesome";
import { Popover } from "react-tiny-popover";
import { Button } from "../Button";
import { Icon } from "../Icon";
import ProfilePopover from "../NavBar/ProfilePopover";
import styles from "./SideNavigation.module.scss";
import SidebarTabs from "./SideNavigationTabs";
import { type SideNavigationProps } from "./types";

const cx = classNames.bind(styles);

/**
 * A navigation component meant to be used beside the main area of an application
 */
const SideNavigation = ({
  tabs,
  activeTab,
  onSignOutClick,
  name,
  version,
  onProfileClick,
  hideFooter,
  isCollapsed = false,
  onCollapsedChange,
  shouldExpandWhenHoveredOrFocused = false,
  widthWhenHoveredOrFocused = 300,
  widthWhenNotHoveredOrFocused = 60,
  className,
  style,
}: SideNavigationProps) => {
  const [openNestedTab, setOpenNestedTab] = useState(activeTab);
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [isHoveringOrFocused, setIsHoveringOrFocused] = useState(false);
  const sidebarRef = useRef<HTMLDivElement>(null);

  const getFirstChar = (text: string) => {
    if (text && typeof text === "string") {
      return text.charAt(0).toUpperCase();
    } else {
      return null;
    }
  };

  useEffect(() => {
    if (!activeTab) return;

    setOpenNestedTab(activeTab);
  }, [activeTab]);

  useEffect(() => {
    if (sidebarRef.current) {
      sidebarRef.current.style.setProperty(
        "--width-when-hovered-or-focused",
        `${widthWhenHoveredOrFocused}px`
      );
    }

    return () => {
      sidebarRef.current?.style.removeProperty(
        "--width-when-hovered-or-focused"
      );
    };
  }, [widthWhenHoveredOrFocused]);

  useEffect(() => {
    if (sidebarRef.current) {
      sidebarRef.current.style.setProperty(
        "--width-when-not-hovered-or-focused",
        `${widthWhenNotHoveredOrFocused}px`
      );
    }

    return () => {
      sidebarRef.current?.style.removeProperty(
        "--width-when-not-hovered-or-focused"
      );
    };
  }, [widthWhenNotHoveredOrFocused]);

  const isActuallyCollapsed = isCollapsed && !isHoveringOrFocused;
  const collapseControl = (
    <Tooltip.Provider>
      <Tooltip.Root>
        <Tooltip.Trigger asChild>
          <Button
            variant="outline"
            className={cx("collapse-control-trigger")}
            slotLeft={
              <Icon
                variant={isCollapsed ? "arrow-line-right" : "arrow-line-left"}
              />
            }
            aria-label={
              isCollapsed ? "Expand Navigation" : "Collapse Navigation"
            }
            onClick={() => {
              onCollapsedChange?.(!isCollapsed);
            }}
          />
        </Tooltip.Trigger>
        <Tooltip.Portal>
          <Tooltip.Content
            className={cx("tooltip-content")}
            sideOffset={12}
            side="right"
          >
            {isCollapsed ? "Expand" : "Collapse"}
            <Tooltip.Arrow className={cx("tooltip-arrow")} />
          </Tooltip.Content>
        </Tooltip.Portal>
      </Tooltip.Root>
    </Tooltip.Provider>
  );

  return (
    <motion.div
      animate={{
        width: isActuallyCollapsed
          ? widthWhenNotHoveredOrFocused
          : widthWhenHoveredOrFocused,
      }}
      ref={sidebarRef}
      className={cx("sidebar", className, {
        sticky: isHoveringOrFocused,
      })}
      style={style}
      tabIndex={0}
      onMouseEnter={() => {
        if (!shouldExpandWhenHoveredOrFocused) return;

        setIsHoveringOrFocused(true);
      }}
      onMouseLeave={() => {
        if (!shouldExpandWhenHoveredOrFocused) return;

        setIsHoveringOrFocused(false);
      }}
      onFocus={() => {
        if (!shouldExpandWhenHoveredOrFocused) return;

        setIsHoveringOrFocused(true);
      }}
      onBlur={() => {
        if (!shouldExpandWhenHoveredOrFocused) return;

        setIsHoveringOrFocused(false);
      }}
    >
      <div className={cx("logo-container", "centered")}>
        {/* Extracted from ValidereBrandmarkDynamic for animation purposes */}
        <motion.svg
          xmlns="http://www.w3.org/2000/svg"
          animate={{
            width: isActuallyCollapsed ? 80 : 375,
            height: isActuallyCollapsed ? 47 : 47,
            viewBox: isActuallyCollapsed ? "0 0 80 47" : "0 0 375 47",
          }}
          transition={{
            type: "just",
            duration: 0.3,
            ease: "easeOut",
          }}
          className={cx("logo")}
        >
          <title>Validere Logo</title>
          <desc>The company Validere&apos;s Logo</desc>
          <path
            fill="currentColor"
            d="M62.411 9.248c-1.841-3.038-3.858-6.179-7.193-7.666-3.2-1.426-7.16-1.124-10.14.718a10.3 10.3 0 0 0-3.234 3.197V5.49a10.5 10.5 0 0 0-1.136 2.36l-9.935 29.508a.58.58 0 0 1-1.104 0C27.498 30.752 19.602 6.42 18.239 2.702H0C2.972 7.6 21.278 37.83 21.28 37.832c1.842 3.038 3.858 6.18 7.194 7.667 3.2 1.425 7.159 1.124 10.14-.719a10.3 10.3 0 0 0 3.233-3.197v.009c.466-.725.854-1.514 1.136-2.36l9.933-29.508a.58.58 0 0 1 1.104 0c2.17 6.604 10.066 30.936 11.43 34.654h18.242C80.72 39.478 62.414 9.245 62.41 9.245z"
          />
          <path
            fill="currentColor"
            fillRule="evenodd"
            d="M242.802 2.686h-13.307v41.976h13.307c12.108 0 21.241-9.021 21.241-20.988S254.91 2.686 242.802 2.686m0 37.15h-8.39V7.451h8.39c9.425 0 16.266 6.821 16.266 16.22s-6.841 16.165-16.266 16.165M181.967 2.743h4.917V39.84h21.009v4.766h-25.926zm33.28 0h4.917v41.862h-4.917zm84.367 41.862h-29.237V2.743h29.237v4.711h-24.32V21.32h22.038v4.653h-22.038v13.867h24.32zm75.148-37.15V2.742h-29.237v41.862h29.237v-4.766h-24.32V25.972h22.038v-4.653h-22.038V7.454zM123.28 37.837l13.943-35.095h5.339l-16.713 41.862h-5.334L104 2.743h5.388zm31.264-35.095-16.713 41.862h5.336l4.123-10.378h19.604l4.109 10.378h5.39L159.875 2.743zm-5.381 26.773 7.95-20.006 7.92 20.006zm189.463-13.812c0 6.596-4.301 11.658-10.706 12.606h-.006q-.075.014-.344.057l11.862 16.237h-6.245L319.14 25.33a.863.863 0 0 1 .694-1.373h5.029c5.408 0 8.903-3.262 8.903-8.31 0-3.807-2.108-8.253-8.046-8.253h-12.475v37.209h-4.918V2.74h17.393c3.883 0 7.183 1.357 9.546 3.92 2.167 2.352 3.36 5.562 3.36 9.044"
            clipRule="evenodd"
          />
        </motion.svg>
      </div>

      <motion.div className={cx("tabs-container")}>
        <div className={cx("tabs-content")}>
          {tabs.map((tab) => {
            return (
              <SidebarTabs
                activeTab={activeTab}
                openNestedTab={openNestedTab}
                setOpenNestedTab={setOpenNestedTab}
                key={tab.id}
                tabDetails={tab}
                isCollapsed={isHoveringOrFocused ? false : isCollapsed}
              />
            );
          })}
        </div>
      </motion.div>

      {!hideFooter ? (
        <div className={cx("footer")}>
          {onCollapsedChange && (
            <div
              className={cx(
                "collapse-controls",
                "is-bordered",
                isCollapsed && "is-collapsed"
              )}
            >
              {collapseControl}
            </div>
          )}
          <div className={cx("profileContainer", { isCollapsed: isCollapsed })}>
            <button className={cx("profileIcon")}>{getFirstChar(name)}</button>

            <div className={cx("greeting")}>
              {name && <div className={cx("name")}>{name}</div>}

              {onProfileClick && (
                <div
                  className={cx("profileLabel")}
                  onClick={onProfileClick}
                >
                  View Profile
                </div>
              )}
            </div>

            <Popover
              isOpen={isPopoverOpen}
              positions={["top", "right"]}
              align="end"
              padding={10}
              reposition
              onClickOutside={() => setIsPopoverOpen(false)}
              containerClassName={cx("popoverContainer")}
              content={
                <ProfilePopover
                  onSignOut={onSignOutClick}
                  onProfileClick={onProfileClick}
                  version={version}
                />
              }
            >
              <button
                className={cx("settingsIcon")}
                aria-label="settings"
                onClick={() => setIsPopoverOpen((isOpen) => !isOpen)}
              >
                <FontAwesome name="cog" />
              </button>
            </Popover>
          </div>
        </div>
      ) : (
        onCollapsedChange && (
          <div
            className={cx(
              "footer",
              "collapse-controls",
              isCollapsed && "is-collapsed"
            )}
          >
            {collapseControl}
          </div>
        )
      )}
    </motion.div>
  );
};

export default SideNavigation;
